import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Mail, RefreshCw, CheckCircle, XCircle, Clock, Loader2, Settings, Folder, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailSourcePage() {
  const { personId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [person, setPerson] = useState(null);
  const [emailSource, setEmailSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isConnectOpen, setIsConnectOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  
  const [connectForm, setConnectForm] = useState({
    email_address: '',
    imap_server: '',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
    folders_to_sync: ['INBOX', 'Sent'],
    sync_from_days: 30
  });

  useEffect(() => {
    fetchPerson();
    fetchEmailSource();
  }, [personId]);

  const fetchPerson = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerson(response.data);
    } catch (error) {
      toast.error('Clone not found');
      navigate('/clones');
    }
  };

  const fetchEmailSource = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}/sources/email/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailSource(response.data);
    } catch (error) {
      // No email source yet - that's fine
      setEmailSource(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      await axios.post(`${API}/persons/${personId}/sources/email/connect`, connectForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email source connected successfully');
      setIsConnectOpen(false);
      fetchEmailSource();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect email');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/persons/${personId}/sources/email/sync`, {
        max_emails: 50
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Sync complete: ${response.data.memories_created} memories created from ${response.data.emails_processed} emails`);
      fetchEmailSource();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect this email source?')) return;
    
    try {
      await axios.delete(`${API}/persons/${personId}/sources/email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email source disconnected');
      setEmailSource(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}/sources/email/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders(response.data.folders);
    } catch (error) {
      toast.error('Failed to fetch folders');
    }
  };

  const statusIcon = {
    pending: <Clock className="w-4 h-4 text-amber-500" />,
    syncing: <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />,
    completed: <CheckCircle className="w-4 h-4 text-emerald-500" />,
    failed: <XCircle className="w-4 h-4 text-rose-500" />
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Email Source">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Email Source">
      <div className="max-w-2xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/clones/${personId}/chat`)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-xl font-heading font-bold">Email Ingestion</h2>
            <p className="text-sm text-muted-foreground">Connect email for {person?.name}</p>
          </div>
        </div>

        {emailSource ? (
          <>
            {/* Connected Source */}
            <Card data-testid="email-source-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{emailSource.email_address}</CardTitle>
                      <CardDescription>{emailSource.imap_server}</CardDescription>
                    </div>
                  </div>
                  <Badge variant={emailSource.is_active ? 'default' : 'secondary'}>
                    {emailSource.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">Sync Status</p>
                    <div className="flex items-center gap-2">
                      {statusIcon[emailSource.last_sync_status]}
                      <span className="font-medium capitalize">{emailSource.last_sync_status}</span>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">Emails Synced</p>
                    <p className="text-2xl font-heading font-bold">{emailSource.emails_synced_count}</p>
                  </div>
                </div>

                {/* Last Sync */}
                <div className="flex items-center justify-between py-3 border-t border-b border-border">
                  <div>
                    <p className="text-sm text-muted-foreground">Last sync</p>
                    <p className="font-medium">{formatDate(emailSource.last_sync_at)}</p>
                  </div>
                  <Button 
                    onClick={handleSync} 
                    disabled={syncing}
                    className="gap-2"
                    data-testid="sync-btn"
                  >
                    {syncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    {syncing ? 'Syncing...' : 'Sync Now'}
                  </Button>
                </div>

                {/* Error Message */}
                {emailSource.last_sync_error && (
                  <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                    <p className="text-sm text-destructive">{emailSource.last_sync_error}</p>
                  </div>
                )}

                {/* Folders */}
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Syncing folders</p>
                  <div className="flex flex-wrap gap-2">
                    {emailSource.folders_to_sync.map((folder) => (
                      <Badge key={folder} variant="secondary" className="gap-1">
                        <Folder className="w-3 h-3" />
                        {folder}
                      </Badge>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Actions */}
                <div className="flex justify-between">
                  <Button variant="outline" onClick={fetchFolders} className="gap-2">
                    <Folder className="w-4 h-4" />
                    View Folders
                  </Button>
                  <Button variant="destructive" onClick={handleDisconnect} className="gap-2" data-testid="disconnect-btn">
                    <Trash2 className="w-4 h-4" />
                    Disconnect
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          /* No Email Source Connected */
          <Card data-testid="connect-email-card">
            <CardContent className="py-12 text-center">
              <Mail className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No email source connected</h3>
              <p className="text-muted-foreground mb-6">
                Connect an email account to automatically ingest emails as memories
              </p>
              
              <Dialog open={isConnectOpen} onOpenChange={setIsConnectOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2" data-testid="connect-email-btn">
                    <Mail className="w-4 h-4" />
                    Connect Email
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Connect Email Source</DialogTitle>
                    <DialogDescription>
                      Connect via IMAP to sync emails automatically
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@company.com"
                        value={connectForm.email_address}
                        onChange={(e) => setConnectForm({ ...connectForm, email_address: e.target.value })}
                        data-testid="email-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="server">IMAP Server</Label>
                        <Input
                          id="server"
                          placeholder="imap.gmail.com"
                          value={connectForm.imap_server}
                          onChange={(e) => setConnectForm({ ...connectForm, imap_server: e.target.value })}
                          data-testid="server-input"
                        />
                        <p className="text-xs text-muted-foreground">Leave empty for auto-detect</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="port">Port</Label>
                        <Input
                          id="port"
                          type="number"
                          value={connectForm.imap_port}
                          onChange={(e) => setConnectForm({ ...connectForm, imap_port: parseInt(e.target.value) })}
                          data-testid="port-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password">App Password *</Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="For Gmail, use an App Password"
                        value={connectForm.imap_password}
                        onChange={(e) => setConnectForm({ ...connectForm, imap_password: e.target.value })}
                        data-testid="password-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Gmail: Use App Password (2FA required) • Outlook: Use regular password or App Password
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Sync emails from last</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          className="w-20"
                          value={connectForm.sync_from_days}
                          onChange={(e) => setConnectForm({ ...connectForm, sync_from_days: parseInt(e.target.value) })}
                          data-testid="days-input"
                        />
                        <span className="text-muted-foreground">days</span>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsConnectOpen(false)}>Cancel</Button>
                    <Button 
                      onClick={handleConnect}
                      disabled={!connectForm.email_address || !connectForm.imap_password}
                      data-testid="submit-connect-btn"
                    >
                      Connect
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        )}

        {/* Available Folders Dialog */}
        {folders.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Available Folders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {folders.map((folder) => (
                  <Badge 
                    key={folder} 
                    variant={emailSource?.folders_to_sync.includes(folder) ? 'default' : 'secondary'}
                    className="gap-1"
                  >
                    <Folder className="w-3 h-3" />
                    {folder}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
